import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, Star } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [priceMax, setPriceMax] = useState(5000);
  const [minRating, setMinRating] = useState(0);
  const [sort, setSort] = useState("featured");
  const activeCategory = params.get("category") || "all";

  useEffect(() => {
    (async () => {
      const [pRes, cRes] = await Promise.all([api.get("/products"), api.get("/categories")]);
      setProducts(pRes.data);
      setCategories(cRes.data);
    })();
  }, []);

  const filtered = useMemo(() => {
    let list = [...products];
    if (activeCategory !== "all") list = list.filter((p) => p.category === activeCategory);
    if (search) list = list.filter((p) => (p.name + " " + p.scent + " " + p.description).toLowerCase().includes(search.toLowerCase()));
    list = list.filter((p) => p.price <= priceMax && p.rating >= minRating);
    switch (sort) {
      case "price-asc": list.sort((a, b) => a.price - b.price); break;
      case "price-desc": list.sort((a, b) => b.price - a.price); break;
      case "rating": list.sort((a, b) => b.rating - a.rating); break;
      case "popular": list.sort((a, b) => b.reviewsCount - a.reviewsCount); break;
      default: break;
    }
    return list;
  }, [products, activeCategory, search, priceMax, minRating, sort]);

  const setCategory = (id) => {
    if (id === "all") { params.delete("category"); }
    else params.set("category", id);
    setParams(params);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <div className="mb-8 sm:mb-10">
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Collection</div>
        <h1 className="font-serif text-4xl sm:text-5xl">Shop Our Candles</h1>
        <p className="text-muted-foreground mt-2 max-w-xl">Explore scents that bring warmth, calm, and a little magic to every room.</p>
      </div>

      {/* Category pills */}
      <div className="flex gap-2 overflow-x-auto pb-3 no-scrollbar mb-6">
        <CatPill label="All" active={activeCategory === "all"} onClick={() => setCategory("all")} tid="filter-all" />
        {categories.map((c) => (
          <CatPill key={c.id} label={c.name} active={activeCategory === c.id} onClick={() => setCategory(c.id)} tid={`filter-${c.id}`} />
        ))}
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input data-testid="shop-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search scents, notes, moods…"
            className="w-full pl-10 pr-4 py-2.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-sm" />
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="shop-sort" className="w-full md:w-56 rounded-full"><SelectValue placeholder="Sort by" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="featured">Featured</SelectItem>
            <SelectItem value="price-asc">Price: Low to High</SelectItem>
            <SelectItem value="price-desc">Price: High to Low</SelectItem>
            <SelectItem value="rating">Highest Rated</SelectItem>
            <SelectItem value="popular">Most Popular</SelectItem>
          </SelectContent>
        </Select>
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" data-testid="shop-filter-trigger" className="rounded-full">
              <SlidersHorizontal className="w-4 h-4 mr-2" /> Filters
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[85%] sm:w-[380px]">
            <SheetHeader><SheetTitle className="font-serif text-2xl">Filters</SheetTitle></SheetHeader>
            <div className="mt-6 space-y-6">
              <div>
                <div className="text-sm font-medium mb-3">Max Price · ₹{priceMax.toLocaleString("en-IN")}</div>
                <Slider min={500} max={5000} step={100} value={[priceMax]} onValueChange={(v) => setPriceMax(v[0])} data-testid="filter-price-slider" />
              </div>
              <div>
                <div className="text-sm font-medium mb-3">Minimum Rating</div>
                <div className="flex gap-2">
                  {[0, 4, 4.5, 4.8].map((r) => (
                    <button key={r} onClick={() => setMinRating(r)}
                      data-testid={`filter-rating-${r}`}
                      className={`px-3 py-1.5 rounded-full text-xs border ${minRating === r ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]" : "border-[hsl(var(--border))]"}`}>
                      {r === 0 ? "Any" : (<><Star className="inline w-3 h-3 mr-1 fill-candle-gold text-candle-gold" />{r}+</>)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="text-sm text-muted-foreground mb-4">{filtered.length} candle{filtered.length !== 1 ? "s" : ""} found</div>

      {filtered.length === 0 ? (
        <div className="py-24 text-center text-muted-foreground">No candles match those filters. Try broadening your search.</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}

const CatPill = ({ label, active, onClick, tid }) => (
  <button data-testid={tid} onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition border ${active ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"}`}>
    {label}
  </button>
);
